import { Type } from '@angular/core';
import { PageSection, ReportType } from '../../../models/end-client-reporting-package';
import { BarChartBlockComponent } from './bar-chart-block.component';
import { CategoricalDistributionBlockComponent } from './categorical-distribution-block.component';
import { DataTableBlockComponent } from './data-table-block.component';
import { SmallMultiplesBlockComponent } from './small-multiples-block.component';
import { DonutChartBlockComponent } from './donut-chart-block.component';
import { ExecutiveSummaryBlockComponent } from './executive-summary-block.component';
import { FunnelBlockComponent } from './funnel-block.component';
import { HeadToHeadBlockComponent } from './head-to-head-block.component';
import { MultiMarketBlockComponent } from './multi-market-block.component';
import { MethodologyBlockComponent } from './methodology-block.component';
import { SingleKpiDialBlockComponent } from './single-kpi-dial-block.component';
import { PieChartBlockComponent } from './pie-chart-block.component';
import { RankedDriverBlockComponent, rankedDriverDrawsOwnCards } from './ranked-driver-block.component';
import { RecommendedActionsBlockComponent } from './recommended-actions-block.component';
import { StackedDistributionBlockComponent } from './stacked-distribution-block.component';
import { VerbatimExplorerBlockComponent } from './verbatim-explorer-block.component';
import { ExplainerPanelBlockComponent } from './explainer-panel-block.component';
import { OwnershipHeatmapBlockComponent } from './ownership-heatmap-block.component';
import { RelationshipLadderBlockComponent } from './relationship-ladder-block.component';
import { PositioningScatterBlockComponent } from './positioning-scatter-block.component';
import { WordCloudBlockComponent } from './word-cloud-block.component';
import { HeatmapBlockComponent } from './heatmap-block.component';
import { TextBlockComponent } from './text-block.component';
import { ImageBlockComponent } from './image-block.component';
import { ReportBlock } from '../report-aggregation';

/**
 * Whether a block draws its own cards, so the host must not put one behind it.
 *
 * Only the tiered ranking does, and only when it has more than one band: each band carries its own
 * heading, so a card around the lot would be a card of cards with two levels of heading. A single
 * band has no heading of its own and takes the host's card like every other module.
 */
export function drawsOwnCards(block: PageSection): boolean {
  return block.reportType === 'tiered-ranking' && rankedDriverDrawsOwnCards(block);
}

export const REPORT_BLOCK_REGISTRY: Record<ReportType, Type<ReportBlock>> = {
  'bar-chart': BarChartBlockComponent,
  'filter-comparison': CategoricalDistributionBlockComponent,
  'subgroup-panels': SmallMultiplesBlockComponent,
  'pie-chart': PieChartBlockComponent,
  'donut-chart': DonutChartBlockComponent,
  'study-overview': MethodologyBlockComponent,
  'headline-stats': ExecutiveSummaryBlockComponent,
  'kpi-score': SingleKpiDialBlockComponent,
  'action-list': RecommendedActionsBlockComponent,
  'stacked-scale': StackedDistributionBlockComponent,
  'funnel': FunnelBlockComponent,
  'segment-tiles': MultiMarketBlockComponent,
  'crosstab': DataTableBlockComponent,
  'diverging-bars': HeadToHeadBlockComponent,
  'tiered-ranking': RankedDriverBlockComponent,
  'open-end-explorer': VerbatimExplorerBlockComponent,
  'word-cloud': WordCloudBlockComponent,
  'scatter-plot': PositioningScatterBlockComponent,
  'ownership-matrix': OwnershipHeatmapBlockComponent,
  'heatmap': HeatmapBlockComponent,
  'stage-matrix': RelationshipLadderBlockComponent,
  'stage-explainer': ExplainerPanelBlockComponent,
  'text': TextBlockComponent,
  'image': ImageBlockComponent,
};

export const REPORT_BLOCK_LABELS: Record<ReportType, string> = {
  'study-overview': 'Study Overview',
  'headline-stats': 'Headline Stats',
  'text': 'Text',
  'image': 'Image',
  'action-list': 'Action List',
  'kpi-score': 'KPI Score',
  'segment-tiles': 'Segment Tiles',
  'bar-chart': 'Bar Chart',
  'pie-chart': 'Pie Chart',
  'donut-chart': 'Donut Chart',
  'stacked-scale': 'Stacked Scale',
  'filter-comparison': 'Filter Comparison',
  'subgroup-panels': 'Subgroup Panels',
  'crosstab': 'Crosstab',
  'diverging-bars': 'Diverging Bars',
  'tiered-ranking': 'Tiered Ranking',
  'funnel': 'Funnel',
  'ownership-matrix': 'Ownership Matrix',
  'heatmap': 'Heatmap',
  'stage-matrix': 'Stage Matrix',
  'stage-explainer': 'Stage Explainer',
  'scatter-plot': 'Scatter Plot',
  'open-end-explorer': 'Open-End Explorer',
  'word-cloud': 'Word Cloud',
};

export const REPORT_BLOCK_GUIDANCE: Record<ReportType, string> = {
  'study-overview': 'How the study was run: sample size, interview length, incidence rate and market, with optional notes. The figures fill themselves in from the data.',
  'headline-stats': 'Four figures with labels you choose, plus one-line takeaways. Nothing is computed — the values are conclusions, so they are typed in.',
  'action-list': 'What to do next, as a short numbered list with a priority on each. Written by hand — the data cannot recommend anything.',
  'text': 'Formatted writing — an introduction, a description of the page, a note on method. Headings, lists and links; no data.',
  'image': 'One uploaded picture — a product, a pack, a piece of creative, a diagram — drawn the width of its slot.',
  'kpi-score': 'One score, computed from answer bands as a net or a share, or typed in. Drawn as a gauge, or as the figure on its own.',
  'segment-tiles': 'One tile per group for a single measure — region, market, customer type. Pick the metric, the question that splits people into groups, and the answers that count.',
  'bar-chart': 'Bars for one question or several. Ranks and trims, averages a numeric battery, colours an ordered scale by band, or groups rows by theme.',
  'pie-chart': 'Shares of a whole for one question, best under about six answers. A question accepting several answers draws as a small donut per option.',
  'donut-chart': 'The same as a Pie Chart, drawn as a ring. A question accepting several answers draws as a small donut per option.',
  'stacked-scale': 'One full-width bar per question on a shared ordered scale — agreement, intent, frequency. Adds a net column, or for a single bar a headline net and share.',
  'filter-comparison': 'Every answer of one question as a pair of bars: everyone against whoever the filters select. For when the gap between a subgroup and the total is the point.',
  'subgroup-panels': 'One question drawn again for each subgroup, as a grid of panels on one shared scale, with a Total panel to read them against.',
  'crosstab': 'One question’s answers down the side, subgroups across the top, a percentage in every cell. For when the exact figures matter more than the shape.',
  'diverging-bars': 'One bar per question running from one side to the other, with any middle answer in between and the gap beside it. For a battery of two-sided choices.',
  'tiered-ranking': 'A ranking question as priorities: each item’s top-N share with its first-place share inside it, sorted and cut into named tiers.',
  'funnel': 'A centred bar per stage, each narrower than the one above, with the drop-off between them. Only where every stage genuinely contains the next.',
  'ownership-matrix': 'Rows by columns, each cell shaded by its share — attributes against brands. Optional columns for what nobody owns and for how much each row matters.',
  'heatmap': 'Where respondents marked an image, shaded over the image: each spot by the share of respondents who picked it. For an image-highlighter question, with the creative it was asked about.',
  'stage-matrix': 'A battery on one shared scale as a grid: a row per item, a column per answer, the columns grouped under named stages, with an optional explainer beside it.',
  'stage-explainer': 'The written panels that explain a Stage Matrix’s stages. Turned on from the matrix’s own settings.',
  'scatter-plot': 'Two measures of the same items plotted against each other, each item a labelled dot, with average lines splitting it into quadrants.',
  'open-end-explorer': 'The written answers to one open-ended question, searchable, paged and narrowed by the filters. For when the wording is the finding.',
  'word-cloud': 'The words people used in one open-ended question, each sized by how many respondents used it. Pairs with the Open-End Explorer.',
};

/**
 * How the module picker groups its options. "Module" rather than "chart" because not all of them
 * are charts -- the study overview reports how the study was run and plots nothing.
 *
 * Split across a Record and an order array on purpose: the Record is exhaustive, so a new module
 * cannot compile until it has been put in a group, which a plain list of groups would let it skip.
 */
export type ReportBlockGroup =
  | 'Summary'
  | 'Text & Images'
  | 'Scores'
  | 'Distributions'
  | 'Comparisons'
  | 'Rankings & Journeys'
  | 'Matrices & Maps'
  | 'Open Ends';

export const REPORT_BLOCK_GROUP_ORDER: readonly ReportBlockGroup[] = [
  'Summary',
  'Text & Images',
  'Scores',
  'Distributions',
  'Comparisons',
  'Rankings & Journeys',
  'Matrices & Maps',
  'Open Ends',
];

const REPORT_BLOCK_GROUP: Record<ReportType, ReportBlockGroup> = {
  'study-overview': 'Summary',
  'headline-stats': 'Summary',
  'action-list': 'Summary',
  'text': 'Text & Images',
  'image': 'Text & Images',
  'kpi-score': 'Scores',
  'segment-tiles': 'Scores',
  'bar-chart': 'Distributions',
  'pie-chart': 'Distributions',
  'donut-chart': 'Distributions',
  'stacked-scale': 'Distributions',
  'filter-comparison': 'Comparisons',
  'subgroup-panels': 'Comparisons',
  'crosstab': 'Comparisons',
  'diverging-bars': 'Comparisons',
  'tiered-ranking': 'Rankings & Journeys',
  'funnel': 'Rankings & Journeys',
  'ownership-matrix': 'Matrices & Maps',
  'heatmap': 'Matrices & Maps',
  'stage-matrix': 'Matrices & Maps',
  'stage-explainer': 'Matrices & Maps',
  'scatter-plot': 'Matrices & Maps',
  'open-end-explorer': 'Open Ends',
  'word-cloud': 'Open Ends',
};

export interface ReportBlockOption {
  value: ReportType;
  label: string;
}

/**
 * Types that exist but cannot be added on their own.
 *
 * The stage explainer is the front half of a stage matrix: the matrix owns the stages and their
 * names, and the explainer is turned on from the matrix's own settings. Adding one by hand
 * would produce a panel with nothing to read its headings from. It stays in the registry, so blocks
 * already stored render as they always did and the generation agent can still write one.
 */
const NOT_ADDABLE: ReadonlySet<ReportType> = new Set<ReportType>(['stage-explainer']);

/**
 * Blocks whose own card is the wash rather than white, because what sits on it is white.
 *
 * The stage matrix and its explainer draw a card per stage. Those stages are the white paper here,
 * so the block behind them has to be the darker of the two or they have nothing to stand on -- the
 * usual arrangement, white card on a washed page, one level in.
 */
const WASHED_CARD_BLOCKS: ReadonlySet<ReportType> =
  new Set<ReportType>(['stage-matrix', 'stage-explainer']);

/** True where the block's own stages are white, so its card is the wash behind them. */
export function cardIsWashed(block: PageSection): boolean {
  return WASHED_CARD_BLOCKS.has(block.reportType);
}

/** The picker's option groups, in order, skipping any group nothing is assigned to. */
export function groupedReportTypes(): { label: ReportBlockGroup; types: ReportBlockOption[] }[] {
  const types = (Object.keys(REPORT_BLOCK_REGISTRY) as ReportType[])
    .filter(type => !NOT_ADDABLE.has(type));
  return REPORT_BLOCK_GROUP_ORDER
    .map(label => ({
      label,
      types: types
        .filter(type => REPORT_BLOCK_GROUP[type] === label)
        .map(value => ({ value, label: REPORT_BLOCK_LABELS[value] })),
    }))
    .filter(group => group.types.length > 0);
}
